import type { TourConfig } from "~/features/onboarding/types/tour-config";

// AIDEV-NOTE: Tour configuration for the session notes page.
//
// The page has NO TABS any more (Sep 2026) — summary, map, parts and commitments are one
// scrolling column, the full story is behind a "Read more" link, and the transcript lives
// in the ••• menu. Every step that pointed at a tab has been retargeted at the thing that
// replaced it; a step whose target no longer exists silently anchors to nothing, so keep
// these selectors in step with the anchors in session-notes.tsx and parts-list.tsx.

export const sessionTourConfig: TourConfig = {
    id: "session-tour",
    page: "/sessions/:sessionId",

    welcome: {
        title: "Explore Your Session Notes",
        description:
            "Take a quick tour to discover how to navigate and use your session notes",
        startButtonText: "Start Tour",
        skipButtonText: "Skip",
    },

    steps: [
        {
            id: "editable-content",
            targetSelector: "[data-tour-id='summary-content']",
            title: "Everything is Editable",
            content:
                "Click on anything in your notes to make edits or additions.",
            position: "bottom",
        },
        {
            id: "full-story-link",
            targetSelector: "[data-tour-id='full-story-link']",
            title: "Full Story",
            content:
                "Read more opens the fuller story of the session — beginning, middle and end.",
            position: "top",
            // Only sessions with a long summary get the link.
            condition: () => {
                return !!document.querySelector(
                    "[data-tour-id='full-story-link']",
                );
            },
        },
        {
            id: "session-parts-map",
            targetSelector: "[data-tour-id='session-parts-map']",
            title: "Session Parts Map",
            content:
                "The parts from this session and how they relate, in Michelle Glass's DPMP® format or the relationship view.",
            position: "top",
            condition: () => {
                return !!document.querySelector(
                    "[data-tour-id='session-parts-map']",
                );
            },
        },
        {
            id: "transcript-menu",
            targetSelector: "[data-tour-id='session-actions-menu']",
            title: "Transcript",
            content:
                "The full transcript lives in this menu, alongside the other session actions.",
            position: "bottom",
            condition: () => {
                return !!document.querySelector(
                    "[data-tour-id='session-actions-menu']",
                );
            },
        },
        {
            id: "part-meditation",
            targetSelector: "[data-tour-id='first-part-meditation']",
            title: "Meditations",
            content:
                "Connect with this Part opens a personalized meditation for it — a way to sit with a part between sessions.",
            position: "top",
            // AIDEV-NOTE: Meditations lost their tab; the way into one is now the part you
            // want to sit with, so this anchors to the first part card's CTA strip.
            condition: () => {
                return !!document.querySelector(
                    "[data-tour-id='first-part-meditation']",
                );
            },
        },
        {
            id: "reminder-button",
            targetSelector: "[data-tour-id='reminder-button']",
            title: "Set a Reminder",
            content: "Schedule a reminder to return to this session later.",
            position: "bottom",
            // AIDEV-NOTE: This step is conditional - only shown if reminder button is present (not for anonymous users)
            condition: () => {
                return !!document.querySelector(
                    "[data-tour-id='reminder-button']",
                );
            },
        },
        {
            id: "first-part",
            targetSelector: "[data-tour-id='first-part-card']",
            title: "View Part Details",
            content:
                "Click on any part from the session to read and/or edit its details.",
            position: "left",
            // AIDEV-NOTE: This step is conditional - only shown if there are parts in the session
            condition: () => {
                return !!document.querySelector(
                    "[data-tour-id='first-part-card']",
                );
            },
        },
    ],
};
